import React, { useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from './styles.module.scss';
import { Popup } from '@components-clean/core/popup/popup';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useOnClickOutside } from '@hooks/index';

export type KebabMenuAction = {
    id: number;
    label: string;
    onClick: () => void;
    icon: IconProp;
    disabled: boolean;
    isRedIcon: boolean;
};

export interface MenuProps {
    actions: KebabMenuAction[];
}

const KebabMenu = ({ actions }: MenuProps) => {
    const { readOnly } = useReadOnlyContext();

    const [popupOpen, setPopupOpen] = useState<boolean>(false);
    const [popupKey, setPopupKey] = useState<number>(0);

    const offSet = useRef(0);
    const anchor = React.useRef<HTMLButtonElement | null>(null);
    const menuItemRef = useRef<HTMLDivElement | null>(null);

    const toggleMenu = () => {
        if (anchor.current) {
            const bodyRect = document.body.getBoundingClientRect();
            const elemRect = anchor.current.getBoundingClientRect();
            offSet.current = bodyRect.bottom - elemRect.top;
            setPopupOpen(!popupOpen);
        }
    };

    useOnClickOutside(menuItemRef as React.RefObject<HTMLDivElement>, () => setPopupOpen(false), [
        anchor as React.RefObject<HTMLElement>,
    ]);

    const onHidden = useEffectEvent(() => {
        if (popupOpen) {
            setPopupOpen(false);
            setPopupKey((prev) => prev + 1);
        }
    });

    useLayoutEffect(() => () => onHidden(), []);

    return (
        <div data-testid="kebab-menu">
            <button
                ref={anchor}
                aria-label="actions"
                type="button"
                onClick={() => toggleMenu()}
                className={cn('k-bg-transparent k-border-0 k-p-0 k-cursor-pointer', styles.button, styles.menu)}
            >
                <FontAwesomeIcon
                    icon={['fas', 'ellipsis-vertical']}
                    fontSize={14}
                    className={cn({ [styles.ellipsisOpen]: popupOpen })}
                />
            </button>
            <Popup
                key={popupKey}
                data-testid="row-menu-popover"
                anchor={anchor.current}
                show={popupOpen}
                anchorAlign={{ horizontal: 'left', vertical: offSet.current < 100 ? 'bottom' : 'top' }}
                popupAlign={{ horizontal: 'right', vertical: 'top' }}
                onBlur={() => setPopupOpen(false)}
                className={styles.popup}
                border="noBorder"
                closeOnBlur={false}
                isWithinActivity
            >
                <div ref={menuItemRef}>
                    {actions.map((action) => (
                        <button
                            aria-label={action.label}
                            key={action.id}
                            disabled={action.disabled || readOnly}
                            type="button"
                            onClick={() => {
                                action.onClick();
                                setPopupOpen(false);
                            }}
                            className={cn(
                                'k-bg-transparent k-border-0 k-p-0 k-d-flex k-align-items-center k-w-full',
                                styles.action,
                                styles.button,
                                {
                                    'k-cursor-pointer': !action.disabled && !readOnly,
                                },
                            )}
                        >
                            <div className="k-w-5.5 k-mr-2 k-flex-shrink-0" style={{ display: 'flex' }}>
                                <FontAwesomeIcon
                                    icon={action.icon}
                                    fontSize={16}
                                    className={cn(action.isRedIcon ? styles.redIcon : styles.icon, {
                                        [styles.disabled]: action.disabled || readOnly,
                                    })}
                                />
                            </div>
                            <span
                                className={cn('k-text-left', {
                                    [styles.disabled]: action.disabled || readOnly,
                                })}
                            >
                                {action.label}
                            </span>
                        </button>
                    ))}
                </div>
            </Popup>
        </div>
    );
};

export default KebabMenu;
